// frontend/src/components/CentrifugeLaser3D.jsx
import React, { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { creerLecteurContinu } from "../utils/continuationPlayback";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import useCentrifugeLaserVisualizer from "../hooks/useCentrifugeLaserVisualizer";

export default function CentrifugeLaser3D({
  running = true,
  chaos = false,
  glow = true,
  spiralSpeedPct = 50,
  ringSpeedPct = 80,
  turboPct = 0, // 🔹 slider Turbo (0 → 200)
  flashRatePct = 30,
  bloomIntensity = 1.2,
  bloomRadius = 0.6,
  bloomThreshold = 0.0,
  onEntropyUpdate = () => {},
  onSnapshot = () => {},
}) {
  const groupRef = useRef();
  // ⚠️ Garde sur `torus` : le hook garantit désormais un objet complet dès le
  // premier rendu, mais une réponse `/initial` partielle pourrait encore
  // fournir un `torus` sans rayons. Un composant de rendu ne doit jamais
  // planter sur une donnée incomplète — il affiche au pire un repère par
  // défaut.
  const scene = useCentrifugeLaserVisualizer({});
  const { spheres, cubes } = scene;
  const torus = scene.torus ?? { major_radius: 1.2, minor_radius: 0.3 };
  const [angle, setAngle] = useState(0);
  const [lumIndex, setLumIndex] = useState(0);

  // Positions de la frame précédente, pour dériver les vitesses réelles.
  // Un ref et non un état : la donnée alimente le POST, pas le rendu — un état
  // provoquerait un re-render par frame pour rien.
  const prevPositionsRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════
  // LE BACKEND POSSÈDE LE MOUVEMENT — docs/DOCTRINE_GEOMETRIE_CANONIQUE.md
  //
  // Ce composant recalculait auparavant chaque position à chaque image, avec
  // ses propres formules (`radius = 5 + Math.sin(...)`). Le tick serveur, lui,
  // faisait tourner l'intégrateur du module. Deux lois de mouvement
  // concurrentes sous le même nom : toute comparaison serveur/navigateur
  // comparait deux objets différents.
  //
  // Le front REJOUE désormais les frames servies par `/animate`, produites par
  // le vrai intégrateur, et interpole entre elles pour rester fluide.
  // ═══════════════════════════════════════════════════════════════════════
  const lecteurRef = useRef(null);  // lecture CONTINUE — SPEC_CONTINUATION.md
  const playheadRef = useRef(0);    // position de lecture, en secondes

  useEffect(() => {
    // ⚠️ Les curseurs sont transmis au MOTEUR, pas appliqués au rejeu.
    // `turboPct` déclenchait une turbulence sur les positions dans l'ancienne
    // boucle de rendu ; elle est rétablie comme paramètre physique du moteur
    // (docs/DOCTRINE_GEOMETRIE_CANONIQUE.md, règle de gouvernance).
    // ⚠️ LECTURE CONTINUE — le rejeu d'un segment unique téléportait la scène
    // à la couture : 198x le pas typique sur ce module.
    const lecteur = creerLecteurContinu({
      url: "/api/geometry/centrifuge_laser/animate",
      parametres: () => ({
        steps: 240,
        dt: 0.05,
        turbulence: turboPct > 100 ? (turboPct - 100) / 100 : 0,
        rotation_speed: 0.02 * (1 + spiralSpeedPct / 100),
      }),
    });
    lecteurRef.current = lecteur;
    lecteur.demarrer();
    return () => {
      lecteur.arreter();
      lecteurRef.current = null;
    };
  }, [turboPct, spiralSpeedPct]);

  /**
   * État interpolé à l'instant de lecture courant.
   *
   * ⚠️ Les deux frames rendues sont TOUJOURS contiguës dans le même segment :
   * le lecteur ne fabrique aucune transition entre segments non adjacents
   * (docs/SPEC_CONTINUATION.md § 6.1).
   */
  function frameInterpolee() {
    const lecteur = lecteurRef.current;
    if (!lecteur) return null;
    const vue = lecteur.lire(playheadRef.current);
    if (!vue) return null;
    if (vue.bascule || vue.gel) playheadRef.current = 0;
    return { a: vue.a, b: vue.b, alpha: vue.alpha };
  }

  /** Interpole une position entre deux frames. */
  function lerpPos(pa, pb, alpha) {
    return [
      pa[0] + (pb[0] - pa[0]) * alpha,
      pa[1] + (pb[1] - pa[1]) * alpha,
      pa[2] + (pb[2] - pa[2]) * alpha,
    ];
  }

  useFrame((_, delta) => {
    if (!running || !groupRef.current) return;

    const speedFactor = spiralSpeedPct / 100;
    const ringFactor = ringSpeedPct / 100;
    const turboFactor = 1 + turboPct / 50; // 0 → 1x, 100 → 3x, 200 → 5x

    // --- Anneau central (rotation inversée, vitesse perso + turbo amplifié) ---
    if (groupRef.current.children[1]) {
      const laserRing = groupRef.current.children[1];
      laserRing.rotation.y -= delta * turboFactor * (4.0 + ringFactor * 6.0);
      laserRing.rotation.x += delta * 0.3 * turboFactor;

      // Multicolore dynamique (HSL cyclique)
      const hue = (Date.now() * 0.05 * ringFactor * turboFactor) % 360;
      const ringColor = new THREE.Color(`hsl(${hue}, 100%, 50%)`);
      laserRing.material.color = ringColor;
      laserRing.material.emissive = ringColor;
      laserRing.material.emissiveIntensity = 1.0 * turboFactor;
    }

    // --- Lecture des frames backend (positions NON recalculées ici) ---
    //
    // ⚠️ LE PLAYHEAD AVANCE AU TEMPS RÉEL. Il était multiplié par
    // `speedFactor * turboFactor * 4.0` : les curseurs pilotaient la CADENCE
    // DE LECTURE. Et ici, contrairement à `torus_spring`, les vitesses du
    // snapshot sont dérivées du `delta` RÉEL (`:180-186`) — accélérer la
    // lecture multipliait donc directement les vitesses MESURÉES, donc la
    // métrique, sans passer par la scène. C'est ce que la règle 2 interdit.
    //
    // Les deux curseurs gardent une cible : `spiralSpeedPct` règle
    // `rotation_speed` et `turboPct` la `turbulence`, tous deux paramètres du
    // MOTEUR dans la requête `/animate` ci-dessus.
    playheadRef.current += delta;
    const interp = frameInterpolee();

    if (interp) {
      const { a, b, alpha } = interp;

      groupRef.current.children
        .filter((child, idx) => child.type === "Mesh" && idx > 1 && idx <= spheres.length + 1)
        .forEach((sphere, index) => {
          const pa = a.spheres[index]?.position;
          const pb = b.spheres[index]?.position;
          if (!pa || !pb) return;
          sphere.position.set(...lerpPos(pa, pb, alpha));
        });

      groupRef.current.children
        .filter((child, idx) => child.type === "Mesh" && idx > spheres.length + 1)
        .forEach((cube, index) => {
          const pa = a.cubes[index]?.position;
          const pb = b.cubes[index]?.position;
          if (pa && pb) cube.position.set(...lerpPos(pa, pb, alpha));
          // La rotation propre du cube reste un effet de rendu : elle ne
          // déplace pas l'objet et n'entre donc dans aucun observable.
          cube.rotation.y += delta * 1.2 * turboFactor;
          cube.rotation.x += delta * 0.7 * turboFactor;
        });
    }

    // --- Rotation centrifuge globale ---
    setAngle((prev) => prev + delta * turboFactor * (0.5 + speedFactor * 3.0));
    groupRef.current.rotation.y = angle * 0.4;

    if (chaos) {
      groupRef.current.rotation.x = Math.sin(angle * 0.5) * 0.3 * turboFactor;
      groupRef.current.position.x = Math.sin(angle * 0.2) * 0.5 * turboFactor;
    } else {
      groupRef.current.rotation.x = 0;
      groupRef.current.position.x = 0;
    }

    // --- Onde lumineuse ---
    setLumIndex(
      (prev) => (prev + delta * turboFactor * (2 + speedFactor * 12)) % (spheres.length + cubes.length)
    );

    // --- Instantané de scène pour l'entropie dynamique ---
    // `dyn_entropy` mesure désormais un état PHYSIQUE (docs/DOCTRINE_ENTROPIE_PHYSIQUE.md).
    // La page envoyait auparavant `rotation_speed`, `dispersion` et
    // `turbulence: chaos ? Math.random() : 0.2` — des curseurs et un tirage,
    // aucune mesure. On remonte ici les positions réellement affichées, et les
    // vitesses dérivées du déplacement entre deux frames.
    const meshes = groupRef.current.children.filter(
      (c, idx) => c.type === "Mesh" && idx > 1
    );
    if (meshes.length) {
      const positions = meshes.map((m) => m.position.toArray());
      const prev = prevPositionsRef.current;
      const dt = Math.max(delta, 1e-6);
      const velocities =
        prev && prev.length === positions.length
          ? positions.map((p, i) => [
              (p[0] - prev[i][0]) / dt,
              (p[1] - prev[i][1]) / dt,
              (p[2] - prev[i][2]) / dt,
            ])
          : positions.map(() => [0, 0, 0]);
      prevPositionsRef.current = positions;
      onSnapshot({ positions, velocities });
    }
  });

  // --- Entropie backend ---
  useEffect(() => {
    let active = true;
    async function fetchEntropy() {
      try {
        const res = await fetch("/api/geometry/centrifuge_laser/entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chaos,
            glow,
            spiral_speed: spiralSpeedPct / 100,
            ring_speed: ringSpeedPct / 100,
            turbo: turboPct / 100,
            flash_rate: flashRatePct / 100,
            bloom_intensity: bloomIntensity,
            bloom_radius: bloomRadius,
            bloom_threshold: bloomThreshold,
          }),
        });
        const data = await res.json();
        if (active && typeof data.entropy === "number") {
          onEntropyUpdate(data.entropy);
        }
      } catch (err) {
        console.error("Erreur fetch entropie CentrifugeLaser:", err);
      }
    }
    fetchEntropy();
    const interval = setInterval(fetchEntropy, 3000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [chaos, glow, spiralSpeedPct, ringSpeedPct, turboPct, flashRatePct, bloomIntensity, bloomRadius, bloomThreshold, onEntropyUpdate]);

  return (
    <>
      <group ref={groupRef}>
        {/* Laser central (halo) */}
        <mesh>
          <sphereGeometry args={[0.4, 32, 32]} />
          <meshPhongMaterial
            color={0x00ffff}
            emissive={0x004444}
            emissiveIntensity={1.2}
            transparent
            opacity={0.9}
          />
        </mesh>

        {/* Anneau central multicolore */}
        <mesh>
          <torusGeometry args={[torus.major_radius, torus.minor_radius, 16, 64]} />
          <meshStandardMaterial
            transparent
            opacity={0.35}
            emissiveIntensity={0.6}
            color={0x00ffff}
            emissive={0x00ffff}
          />
        </mesh>

        {/* Sphères satellites */}
        {spheres.map((sphere, i) => {
          const isLit = Math.floor(lumIndex) === i;
          const color = new THREE.Color(...sphere.color);
          return (
            <mesh key={`sphere-${i}`} position={sphere.position}>
              <sphereGeometry args={[sphere.radius, 20, 20]} />
              <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={isLit ? 2.0 : glow ? 0.3 : 0}
              />
            </mesh>
          );
        })}

        {/* Cubes satellites */}
        {cubes.map((cube, i) => {
          const isLit = Math.floor(lumIndex - spheres.length) === i;
          const color = new THREE.Color(...cube.color);
          return (
            <mesh key={`cube-${i}`} position={cube.position}>
              <boxGeometry args={[cube.size, cube.size, cube.size]} />
              <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={isLit ? 2.0 : glow ? 0.3 : 0}
              />
            </mesh>
          );
        })}
      </group>

      <EffectComposer>
        <Bloom intensity={bloomIntensity} radius={bloomRadius} threshold={bloomThreshold} />
      </EffectComposer>
    </>
  );
}
